import { useNotification } from "@refinedev/core";
import { Alert, Button, Drawer, Form, Input, Popconfirm, Space, Spin } from "antd";
import { useEffect, useState, type FC } from "react";
import { axiosInstance } from "../../../api/client";
import type { EnvironmentCredentials } from "../../../types";

interface CredentialsDrawerProps {
  projectId: string | null;
  envId: string | null;
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

export const CredentialsDrawer: FC<CredentialsDrawerProps> = ({ projectId, envId, open, onClose, isMobile }) => {
  const [form] = Form.useForm();
  const { open: notify } = useNotification();

  const [existingCreds, setExistingCreds] = useState<EnvironmentCredentials | null>(null);
  // Tracks which project/env we've last fetched credentials for, so "loading" can be derived
  // instead of set synchronously at the top of the effect below.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currentKey = open && projectId && envId ? `${projectId}:${envId}` : null;
  const loading = currentKey !== null && loadedKey !== currentKey;

  useEffect(() => {
    if (!currentKey || !projectId || !envId) return;
    let cancelled = false;
    axiosInstance
      .get<EnvironmentCredentials | null>(`/projects/${projectId}/environments/${envId}/credentials`)
      .then((res) => {
        if (cancelled) return;
        setExistingCreds(res.data);
        if (res.data) form.setFieldsValue({ username: res.data.username });
        setLoadedKey(currentKey);
      })
      .catch(() => {
        if (cancelled) return;
        setExistingCreds(null);
        setLoadedKey(currentKey);
      });
    return () => {
      cancelled = true;
    };
  }, [currentKey, projectId, envId, form]);

  const handleClose = () => {
    onClose();
    setExistingCreds(null);
    setLoadedKey(null);
    form.resetFields();
  };

  const handleSubmit = async (values: { username: string; password: string }) => {
    if (!projectId || !envId) return;
    setSubmitting(true);
    try {
      await axiosInstance.put(`/projects/${projectId}/environments/${envId}/credentials`, values);
      notify?.({ type: "success", message: "Credentials saved" });
      handleClose();
    } catch {
      notify?.({ type: "error", message: "Failed to save credentials" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!projectId || !envId) return;
    try {
      await axiosInstance.delete(`/projects/${projectId}/environments/${envId}/credentials`);
      notify?.({ type: "success", message: "Credentials removed" });
      handleClose();
    } catch {
      notify?.({ type: "error", message: "Failed to remove credentials" });
    }
  };

  return (
    <Drawer title="WordPress credentials" open={open} onClose={handleClose} width={isMobile ? "100%" : 480}>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          {existingCreds && (
            <Alert
              type="info"
              showIcon
              message={`Current username: ${existingCreds.username}`}
              description="Re-enter both fields to update the credentials."
              style={{ marginBottom: 16 }}
            />
          )}
          <Form form={form} layout="vertical" onFinish={(values) => void handleSubmit(values)}>
            <Form.Item
              name="username"
              label="WordPress username"
              rules={[{ required: true, message: "Username is required" }]}
            >
              <Input placeholder="admin" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Application password"
              extra="Generate one in WordPress: Users, Profile, Application Passwords."
              rules={[{ required: true, message: "Password is required" }]}
            >
              <Input.Password placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" />
            </Form.Item>
            <Space style={{ width: "100%" }} direction="vertical">
              <Button type="primary" htmlType="submit" loading={submitting} block>
                Save credentials
              </Button>
              {existingCreds && (
                <Popconfirm
                  title="Remove credentials"
                  description="Remove WordPress credentials for this environment?"
                  onConfirm={() => void handleDelete()}
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                >
                  <Button danger block>
                    Remove credentials
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form>
        </>
      )}
    </Drawer>
  );
};
